import type { VirtualAppointmentOpenNowPlan } from '../../services/virtualAppointments';
import type { AlignerProgress } from './virtualAppointmentTriggerBaseline';
import { alignerLimitOf } from './virtualAppointmentTriggerAhead';
import { DEFAULT_OPEN_NOW_MODE, weeksStartUnavailable, type OpenNowMode } from './openNowMode';
import {
    createEmptyDraft,
    draftToPlan,
    draftToPlanContent,
    triggerContextFor,
    validateDraft,
    validateDraftContent,
    type VirtualAppointmentDraft,
    type VirtualAppointmentDraftErrors,
    type VirtualAppointmentDraftPatch
} from './virtualAppointmentDraft';

// A new VA in "Add VAs": it opens at once, or is planned with the usual trigger.
export interface OpenNowDraft extends VirtualAppointmentDraft {
    mode: OpenNowMode;
}

export type OpenNowDraftPatch = VirtualAppointmentDraftPatch & { mode?: OpenNowMode };

// What the trigger field needs to know about the patient.
export interface OpenNowPatientContext {
    /** UTC day the Treatment phase began; null when none is on record. */
    treatmentStartDate: string | null;
    dmLinked: boolean;
    aligners: AlignerProgress;
    /** Tx-0 to Tx-3 only; anywhere else a new VA can only be planned. */
    canOpenNow: boolean;
}

export function createOpenNowDraft(): OpenNowDraft {
    return { ...createEmptyDraft(), mode: DEFAULT_OPEN_NOW_MODE };
}

// As listed: an open-now VA reads "Opened now", a planned one its trigger.
export function listedDraft(draft: OpenNowDraft): VirtualAppointmentDraft {
    return draft.mode === 'now' ? { ...draft, triggerKind: 'now' } : draft;
}

type OpenNowValidationContext = Pick<
    OpenNowPatientContext,
    'treatmentStartDate' | 'dmLinked' | 'canOpenNow'
> &
    Partial<Pick<OpenNowPatientContext, 'aligners'>>;

// A planned one follows the Add VA rules: week still ahead, ranges, aligner count, DM link for no-gos.
export function validateOpenNowDraft(
    draft: OpenNowDraft,
    patient: OpenNowValidationContext
): VirtualAppointmentDraftErrors {
    if (draft.mode === 'now') return validateDraftContent(draft);
    const context = triggerContextFor(patient.treatmentStartDate, patient.dmLinked, {
        alignerLimit: alignerLimitOf(patient.aligners?.total),
        weeksUnavailable: weeksStartUnavailable(patient)
    });
    return validateDraft(draft, context);
}

// Callers must validate first. No trigger means the server opens it at once.
export function openNowDraftToPlan(draft: OpenNowDraft): VirtualAppointmentOpenNowPlan {
    const content = draftToPlanContent(draft);
    if (draft.mode === 'now') return content;
    const plan = draftToPlan(draft);
    switch (plan.triggerKind) {
        case 'aligner':
            return { ...content, triggerKind: 'aligner', triggerAligner: plan.triggerAligner };
        case 'weeks':
            return {
                ...content,
                triggerKind: 'weeks',
                triggerWeeks: plan.triggerWeeks,
                weeksFrom: plan.weeksFrom
            };
        case 'nogos':
            return { ...content, triggerKind: 'nogos', triggerNogos: plan.triggerNogos };
        case 'now':
            return content;
    }
}

export interface OpenNowCounts {
    open: number;
    plan: number;
}

export function countOpenNowModes(drafts: readonly Pick<OpenNowDraft, 'mode'>[]): OpenNowCounts {
    const open = drafts.filter((d) => d.mode === 'now').length;
    return { open, plan: drafts.length - open };
}
